import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Events } from '@df/core/service/event';
import { TimeoutService } from '@df/ng/timeout.service';
import { BasketService, IAddToBasketSuccessEventData, IRemoveFromBasketSuccessEventData } from '@df/session/api/basket/basket.service';
import defaultsDeep from 'lodash-es/defaultsDeep';
import { filter, first, lastValueFrom } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import { AdvancedCommerce } from '../advanced-commerce.namespace';
import { AdvancedCommerceService } from '../advanced-commerce.service';
import { AdvancedCommerceConfigStore } from '../store/advanced-commerce-config.store';

export function initAdvancedCommerceTrackService(service: AdvancedCommerceTrackService) {
    return async () => service.init();
}

@Injectable({
    providedIn: 'root'
})
export class AdvancedCommerceTrackService implements OnDestroy {
    protected subscription = new Subscription();

    protected trackBasketThrottled!: (event: IAddToBasketSuccessEventData) => void;
    protected trackBasketRemoveThrottled!: (event: IRemoveFromBasketSuccessEventData) => void;
    protected trackOrderSuccessThrottled!: (event: Checkout.ISuccessData) => void;

    /**
     * Last path we tracked content view for
     */
    protected lastPath?: string;

    protected http = inject(HttpClient);
    protected basketService = inject(BasketService);
    protected app = inject(App);
    protected timeoutService = inject(TimeoutService);
    protected events = inject(Events);
    protected productResource = inject(ProductResource);
    protected advancedCommerceService = inject(AdvancedCommerceService);
    protected location = inject(Location);
    protected configStore = inject(AdvancedCommerceConfigStore);

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    async init(): Promise<void> {
        await this.configStore?.loadConfig();

        if (this.configStore?.config()?.enabled) {
            this.trackBasketThrottled = this.timeoutService.throttle(event => this.trackBasket(event), 200, {
                leading: false,
                trailing: true
            });

            this.trackBasketRemoveThrottled = this.timeoutService.throttle(event => this.trackBasketRemove(event), 200, {
                leading: false,
                trailing: true
            });

            this.trackOrderSuccessThrottled = this.timeoutService.throttle(event => this.trackOrder(event), 200, {
                leading: false,
                trailing: true
            });

            // track adding to basket
            this.subscription.add(this.basketService.addToBasketSuccessSubject.subscribe(event => this.trackBasketThrottled(event)));

            this.subscription.add(
                this.basketService.removeFromBasketSuccessSubject.subscribe(event => this.trackBasketRemoveThrottled(event))
            );

            // track successful order placement
            this.subscription.add(
                this.events.on(Checkout.EVENTS.SuccessTracking).subscribe(event => this.trackOrderSuccessThrottled(event))
            );

            // track product view first enter page (no click tracking before)
            this.subscription.add(
                this.app.meta.subject
                    .pipe(
                        filter(e => !!e?.model),
                        first()
                    )
                    .subscribe(e => {
                        if (e?.model?.type === 'product' && !this.app.meta.previousState) {
                            this.trackView(e?.model as Product);
                        }
                    })
            );
        }
    }

    trackBasket(event: IAddToBasketSuccessEventData): void {
        const data = this.getData<AdvancedCommerce.Track.DataBasket[]>({
            scope: AdvancedCommerce.Track.Scope.basket,
            action: AdvancedCommerce.Track.ActionBasket.add,
            data: []
        });

        event.request.products.forEach(product => {
            const id = this.app.resources.product.get(product.id).parent.id || product.id;

            data.data.push({
                product_id: `${id}`,
                quantity: product.qty
            });
        });

        this.track(data);
    }

    trackBasketRemove(event: IRemoveFromBasketSuccessEventData): void {
        const data = this.getData<AdvancedCommerce.Track.DataBasket[]>({
            scope: AdvancedCommerce.Track.Scope.basket,
            action: AdvancedCommerce.Track.ActionBasket.delete,
            data: []
        });

        event.items.forEach(item => {
            const id = item.product?.parent?.id || item.data.product;

            data.data.push({
                product_id: `${id}`,
                quantity: item.data.request.qty
            });
        });

        this.track(data);
    }

    trackOrder(event: Checkout.ISuccessData): void {
        const data = this.getData<AdvancedCommerce.Track.DataOrder>({
            scope: AdvancedCommerce.Track.Scope.order,
            action: AdvancedCommerce.Track.ActionBasket.add,
            data: {
                order_id: `${event.number}`,
                order_total: event.total,
                order_currency: `${event.currency}`,
                order_lines: []
            }
        });

        if (event['totals']) {
            data.data.order_shipping = event['totals'].delivery;
            data.data.order_tax = event['totals'].tax;
        }

        event.items?.forEach(item => {
            const product = this.productResource.get(item.product_id);
            const line: AdvancedCommerce.Track.DataOrderLine = {
                product_id: `${item.product_id}`,
                product_sku: item['sku'] || product.data?.sku || '',
                product_name: item['name'] || product.name!,
                quantity: item.qty_ordered,
                order_line_total: item['rowPrice'] || product.price * item.qty_ordered || 0
            };

            if (product.parent.isConfigurable) {
                line.product_id = `${product.parent.id}`;

                if (product.parent.data?.sku) {
                    line.product_sku = product.parent.data?.sku;
                }

                line.variant_id = `${item.product_id}`;
                line.variant_sku = item['sku'] || product.data?.sku;
            }

            data.data.order_lines.push(line);
        });

        this.track(data);
    }

    trackClick(value: Partial<AdvancedCommerce.Track.RequestData<AdvancedCommerce.Track.DataView>>): void {
        const data = this.getData<AdvancedCommerce.Track.DataView>({
            scope: AdvancedCommerce.Track.Scope.view,
            action: AdvancedCommerce.Track.ActionBasket.add,
            ...value
        });
        this.track(data);
    }

    trackView(model: Product): void {
        const data = this.getData<AdvancedCommerce.Track.DataView>({
            scope: AdvancedCommerce.Track.Scope.view,
            action: AdvancedCommerce.Track.ActionBasket.add,
            data: {
                product_id: `${model.parent?.id || model.id}`
            }
        });
        this.track(data);
    }

    protected getData<T>(data: Partial<AdvancedCommerce.Track.RequestData<T>>) {
        return defaultsDeep(data, {});
    }

    protected track(data: AdvancedCommerce.Track.RequestData) {
        return lastValueFrom(this.advancedCommerceService.post(data, AdvancedCommerce.Api.Type.track));
    }
}
